import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import qs from "query-string";
import { withPageInitialLoad } from "../../libs/pageDataBridge";
import {
  selectPDPBannerSections,
  selectPDPProductInfo,
  selectProductDetails,
  selectProductParents,
  selectProductSizeChart
} from "./selectors";
import { selectLanguageSlug, selectSyteSettings } from "../../util/selectors";
import { isTouchScreen } from "../../util";
import { toastMsg } from "@/util/toast";
import { SECTION_SLUGS } from "../../constants/sections";
import { setRecentlyViewedProducts } from "../../redux/actions/common.action";
import {
  fetchProductDetail,
  fetchSizeChart,
  resetProductDetail
} from "../../redux/actions/productDetail.action";
import {
  AlgoliaInsightsService,
  CommonService,
  DynamicYieldService
} from "../../services";
import { withDeliverySettingsFetching } from "../../hocs";
import { getBannerSections } from "../../redux/actions/section.action";
import useBodyScrollability from "../../hooks/useBodyScrollability";
import useProductSizeSelectionFlow from "@/components/product/hooks/useProductSizeFlow";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { handleAddToCart } from "../../redux/actions/cart.action";
import ProductDetailsLayout from "./productDetailsLayout";
import { Product } from "../../services/productService";
import {
  useCountryId,
  useIsRTL,
  useLongLanguage,
  useRouteSlug
} from "../../hooks";
import { checkResponseStatus, runSafely } from "../../util/common";
import PdpAnalyticsEvent from "../../services/analytics/main/pdp";
import CommonAnalyticsEvent from "@/services/analytics/main/common";
import {
  productContext,
  productErrorContext
} from "@/components/product/webSizes/productWebSizes";
import { TOAST_TYPES } from "../../constants";

type LocationStatePDP = {
  fromPLP?: boolean;
  isAvailableColorItem?: boolean;
};

const ProductDetailsContainer = ({ payLaterOptions }) => {
  const { enableBodyScroll, disableBodyScroll } = useBodyScrollability();
  const productSizeChart = useAppSelector(selectProductSizeChart);
  const [showProductSizeChart, setShowProductSizeChart] = useState(false);
  const [openMultipleSizes, setOpenMultipleSizes] = useState(false);

  const cartItems = useAppSelector(state => state.cartReducer?.cart);

  const [showShippingOptions, setShowShippingOptions] = useState(false);
  const [selectSizeFirstError, setSelectSizeFirstError] = useState(false);
  const [isSuccessBlockOpen, setIsSuccessBlockOpen] = useState(false);
  const [sameSizeInCartCount, setSameSizeInCartCount] = useState(0);

  const language = useAppSelector(selectLanguageSlug);
  const fullLanguage = useLongLanguage();
  const countryId = useCountryId();
  const { productId, captionImageURL } = useAppSelector(selectPDPProductInfo);
  const { subcategory } = useAppSelector(selectProductParents);
  const item = useAppSelector(selectProductDetails) as Product;
  const [topSections, firstSections, secondSections] = useAppSelector(
    selectPDPBannerSections
  );
  const configSyteSettings = useAppSelector(selectSyteSettings);
  const history = useHistory();
  const routeSlug = useRouteSlug();
  const location = useLocation<LocationStatePDP>();
  const isAvailableColorItem = location.state?.isAvailableColorItem;
  const {
    activeSizeType,
    setActiveSizeType,
    selectedSizeFullData,
    setSelectedSizeFullData
  } = useProductSizeSelectionFlow(item);
  const [selectedQty, setSelectedQty] = useState(1);
  const [onSizeSelectionHandler, setOnSizeSelectionHandler] = useState(
    () => setSelectedSizeFullData
  );
  const [alertMessage, setAlertMessage] = useState("");

  const dispatch = useAppDispatch();
  const itemDetailWrapperRef = useRef(null);
  const isRTL = useIsRTL();

  const algoliaQueryIdFromLocation = qs.parse(location.search)?.queryId;
  const algoliaOriginFromLocation = qs.parse(location.search)?.origin;

  useEffect(() => {
    if (isEmpty(item)) return;
    const { id, captionImageURL, title, url, sizes } = item;
    if (!item.pstock) {
      PdpAnalyticsEvent.outOfStock({
        item
      });
    }

    setRecentlyViewedProducts({
      id,
      captionImageURL,
      title: title?.label ?? "",
      url
    });

    CommonAnalyticsEvent.viewProduct({
      product: item
    });
  }, [item]);

  useEffect(() => {
    if (item.parentCategory && item.subcategory) {
      dispatch(
        fetchSizeChart(
          countryId,
          item.parentCategory.id,
          item.brand.id,
          item.subcategory.id,
          fullLanguage
        )
      );
    }

    selectSizeFirstError && setSelectSizeFirstError(false);
  }, [item]);

  useEffect(() => {
    if (!isEmpty(selectedSizeFullData.size)) {
      PdpAnalyticsEvent.selectSize({
        item,
        sizeData: selectedSizeFullData
      });

      selectedSizeFullData?.size?.upc &&
        DynamicYieldService.setDYHead("pdp", language, [
          selectedSizeFullData.size.upc
        ]);
    }
  }, [selectedSizeFullData]);

  useEffect(() => {
    if (isAvailableColorItem && !isEmpty(item)) {
      PdpAnalyticsEvent.select({
        item: { ...item, position: null },
        listHeading: undefined
      });
      const { state, ...locationUrlParts } = location;
      const { isAvailableColorItem, ...restState } = state;
      history.replace(locationUrlParts, restState);
    }
  }, [isAvailableColorItem]);

  const openSuccessAddedToCartBlock = () => {
    const isResponsiveView = isTouchScreen();

    if (isResponsiveView) {
      setIsSuccessBlockOpen(true);
      disableBodyScroll();
    }
  };

  useEffect(() => {
    let script = null;
    if (item.id) {
      DynamicYieldService.setDYHead("pdp", language, [
        item.allSizes.map(size => size.upc)[0]
      ]);
    }
    return () => {
      script?.remove();
    };
  }, [item]);

  useEffect(() => {
    return () => {
      enableBodyScroll();
    };
  }, []);

  const closeSuccessAddedToCartBlock = useCallback(() => {
    setOnSizeSelectionHandler(() => setSelectedSizeFullData);
    setIsSuccessBlockOpen(false);
    enableBodyScroll();
  }, [
    setOnSizeSelectionHandler,
    setSelectedSizeFullData,
    setIsSuccessBlockOpen,
    enableBodyScroll
  ]);

  const onAddToCartClickHandler = isEmpty(selectedSizeFullData.size)
    ? () => {
        const handleAddToCartCallback = data => {
          setSelectedSizeFullData(data);
          addToCart(getCartDataFromEvent(data));
        };

        setOpenMultipleSizes(true);
        setOnSizeSelectionHandler(() => handleAddToCartCallback);
      }
    : () => addToCart(getCartData());

  // This function for addToCart button when we don't have selected size
  const getCartDataFromEvent = data => {
    const size = data.size?.size?.trim();
    const sizeType = activeSizeType.defaultValue;
    const sizeId = data?.size?.sizeId;
    const sizeTypeKey = activeSizeType.key;

    return {
      productId: item.id,
      quantity: selectedQty,
      size,
      sizeId,
      sizeType,
      sizeTypeKey,
      isMultisize: item.isMultisize
    };
  };

  // This function for addToCart button when we have selected size
  const getCartData = () => {
    const size = selectedSizeFullData.size?.size?.trim();
    const sizeType = activeSizeType.defaultValue;
    const sizeTypeKey = activeSizeType.key;
    const sizeId = selectedSizeFullData?.size?.sizeId;

    return {
      productId: item.id,
      quantity: selectedQty,
      size,
      sizeId,
      sizeType,
      sizeTypeKey,
      isMultisize: item.isMultisize,
      productPrice: item.price.current,
      algoliaQueryId: algoliaQueryIdFromLocation,
      origin: algoliaOriginFromLocation,
      upc: selectedSizeFullData?.size?.upc
    };
  };

  const checkResponseStatusCode = response => {
    const { message, isAlertMessage, isNetworkError } =
      checkResponseStatus(response);

    if (isAlertMessage) {
      setAlertMessage(message);
    }
    if (isNetworkError) {
      toastMsg(TOAST_TYPES.ERROR, message, true);
    }
  };

  const addToCart = async cartData => {
    const response = await dispatch(handleAddToCart(cartData, language));

    checkResponseStatusCode(response);

    PdpAnalyticsEvent.addToCart({
      item,
      cartData
    });
    const itemWithTheSameSizeInCart = find(cartItems, {
      sizeId: cartData.sizeId,
      id: cartData.productId
    });
    if (itemWithTheSameSizeInCart)
      setSameSizeInCartCount(itemWithTheSameSizeInCart.quantity);

    const isError = response && response instanceof Error;

    !isError && openSuccessAddedToCartBlock();

    return response;
  };

  const goToCart = () => history.push(`/${routeSlug}/my-cart/`);

  useEffect(() => {
    return () => {
      dispatch(resetProductDetail());
    };
  }, []);
  return (
    <productContext.Provider value={item}>
      <productErrorContext.Provider
        value={{
          selectSizeFirstError,
          setSelectSizeFirstError
        }}
      >
        <ProductDetailsLayout
          topSections={topSections}
          firstSections={firstSections}
          secondSections={secondSections}
          language={language}
          productId={productId}
          itemDetailWrapperRef={itemDetailWrapperRef}
          activeSizeType={activeSizeType}
          setActiveSizeType={setActiveSizeType}
          selectedSizeFullData={selectedSizeFullData}
          setSelectedSizeFullData={setSelectedSizeFullData}
          setShowProductSizeChart={setShowProductSizeChart}
          setShowShippingOptions={setShowShippingOptions}
          setSelectedQty={setSelectedQty}
          selectedQty={selectedQty}
          onAddToCartClickHandler={onAddToCartClickHandler}
          addToCart={addToCart}
          getCartData={getCartData}
          item={item}
          isSuccessBlockOpen={isSuccessBlockOpen}
          closeSuccessAddedToCartBlock={closeSuccessAddedToCartBlock}
          sameSizeInCartCount={sameSizeInCartCount}
          payLaterOptions={payLaterOptions}
          onSizeSelectionHandler={onSizeSelectionHandler}
          openMultipleSizes={openMultipleSizes}
          setOpenMultipleSizes={setOpenMultipleSizes}
          countryId={countryId}
          subcategory={subcategory}
          captionImageURL={captionImageURL}
          configSyteSettings={configSyteSettings}
          productSizeChart={productSizeChart}
          showProductSizeChart={showProductSizeChart}
          showShippingOptions={showShippingOptions}
          goToCart={goToCart}
          alertMessage={alertMessage}
          setAlertMessage={setAlertMessage}
          isRTL={isRTL}
        />
      </productErrorContext.Provider>
    </productContext.Provider>
  );
};

export default withPageInitialLoad({
  pageName: "pdp",
  page: withDeliverySettingsFetching(ProductDetailsContainer),
  strategy: async ({ store, params, ipcountry }) => {
    const [payLaterOptions] = await Promise.all([
      runSafely(() => CommonService.getPayLaterOptionsList()),
      store.dispatch(fetchProductDetail(params.id, ipcountry)),
      runSafely(() => store.dispatch(getBannerSections(SECTION_SLUGS.PDP)))
    ]);

    return {
      payLaterOptions
    };
  },
  defaultInitialProps: {
    payLaterOptions: []
  },
  loader: true,
  ssr: true
});
